import { ApolloClient, NormalizedCacheObject } from '@apollo/client'
import * as Sentry from '@sentry/browser'
import type { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios'
import axios from 'axios'
import type { Environment } from 'config/environment'
import once from 'lodash/once'

import { convertIdsToBigint } from 'modules/core/lib/convertIdsToBigint'
import { TrackingData, buildTrackingData } from 'modules/core/lib/buildTrackingData'
import { logNetworkError } from 'modules/core/lib/datadogLogger'
import {
  BusinessError,
  NetworkError,
  RequestCancelledError,
  TentacleError,
  ValidationError,
} from 'modules/core/lib/errors'
import {
  refreshFactorialIdToken,
  isFactorialIdUser,
} from 'modules/core/lib/factorialIdTokenRefresh'
import i18n from 'modules/core/lib/i18n'
import { track } from 'modules/core/lib/metrics'
import {
  isTentacleError,
  isValidationErrorData,
  parseErrorData,
} from 'modules/core/lib/parseErrorData'
import app from 'modules/core/store/models/app'

// Extend Axios request config to include retry flag
declare module 'axios' {
  export interface InternalAxiosRequestConfig {
    _retry?: boolean
  }
}

const waitSentry = async () => {
  const client = Sentry.getCurrentHub().getClient()

  if (client) await client.close(2000)
}

export const sessionExpired = once(async ({ apiEndpoint, factorialIdEndpoint }: Environment) => {
  // Check if user is using Factorial ID (has the _factorial_id_data cookie)
  const hasFactorialIdCookie = document.cookie
    .split(';')
    .some((cookie) => cookie.trim().startsWith('_factorial_id_data='))

  if (hasFactorialIdCookie && factorialIdEndpoint) {
    // For Factorial ID users, redirect to Factorial ID login
    await waitSentry()
    window.location.href = `${factorialIdEndpoint}/login`
    return
  }

  // For legacy auth users, redirect to old login
  const href = encodeURIComponent(window.location.href)
  const search = window.location.search
  const queryParams = new URLSearchParams(search)

  const defaultLocale = 'en'
  const queryLocale = queryParams.get('locale')

  const localeToUse = queryLocale || defaultLocale
  const signInUrl = `${apiEndpoint}/${localeToUse}/users/sign_in`

  await waitSentry()

  window.location.href = `${signInUrl}${search || '?'}&return_to=${href}`
})

const differentUser = once(async (apolloClient: ApolloClient<NormalizedCacheObject>) => {
  const message = i18n.t('errors.different_user')

  app.setFlash({
    type: 'accent',
    message,
  })

  track(apolloClient, 'account switched', {})
  await waitSentry()
  window.location.reload()
})

const reportToDatadog = (
  trackingData: TrackingData | null,
  environment: Environment,
  error: AxiosError
) => {
  if (!trackingData) {
    return
  }

  const { message, response } = error
  const requestId = response?.headers['x-amzn-trace-id'] ?? 'unknown'
  const node = response?.headers['x-factorial-node'] ?? 'unknown'
  const releaseVersion = environment.releaseVersion

  logNetworkError(
    {
      message,
      requestId,
      node,
      releaseVersion,
    },
    trackingData
  )
}

const addBreadcrumbToSentry = (error: AxiosError) => {
  const { config, response, message } = error
  const status = response?.status ?? 0
  const requestId = response?.headers['x-amzn-trace-id'] ?? 'unknown'
  const node = response?.headers['x-factorial-node'] ?? 'unknown'
  const errorMessage = `${config.url} was ${status}`
  const level = 'error'
  Sentry.setExtra('errorMessage', message)
  Sentry.setExtra('httpStatus', status)
  Sentry.setExtra('requestId', requestId)
  Sentry.setExtra('node', node)
  Sentry.addBreadcrumb({
    category: 'xhr',
    message: errorMessage,
    level,
  })
}

const beforeRequest =
  (apolloClient: ApolloClient<NormalizedCacheObject>, release: string) =>
  async (config: AxiosRequestConfig) => {
    const trackingData = await buildTrackingData(apolloClient)

    if (!trackingData) {
      return config
    }

    config.headers['X-Factorial-Version'] = release // Expose the frontend version to the backend
    config.headers['X-Factorial-Access'] = trackingData.userId

    return config
  }

const SKIP_ERROR = Promise.resolve({ data: {} })

const beforeResponse =
  (apolloClient: ApolloClient<NormalizedCacheObject>) => (response: AxiosResponse) => {
    if (response?.headers['x-factorial-broken-version'] === 'force-reload') {
      window.location.reload()
      return
    }

    if (response?.headers['x-factorial-broken-version'] === 'force-invalidation') {
      apolloClient.resetStore()
    }
    // NOTE: investigation in progress. We want to know which backend version
    // served the feature flags endpoint
    const url = response?.config.url

    if (url && url.match(/features/)) {
      const version = response?.headers['x-factorial-version'] ?? 'unknown' // Retrieve the backend version for Sentry

      Sentry.setExtra('factorial-version-features', version)
    }

    return response
  }

const onError =
  (environment: Environment, apolloClient: ApolloClient<NormalizedCacheObject>) =>
  async (error: AxiosError<object | string[] | string>) => {
    const trackingData = await buildTrackingData(apolloClient)

    // throws a custom error RequestCancelledError in order to avoid this kind of exception on Sentry
    if (axios.isCancel(error)) {
      throw new RequestCancelledError()
    }

    addBreadcrumbToSentry(error)

    if (!error.response) {
      throw new NetworkError(error.message)
    }

    const responseStatus = error.response.status
    const responseData = error.response.data

    if (responseStatus === undefined) {
      // NOTE: this looks like an abort
      throw error
    } else if (responseStatus === 401) {
      const originalRequest = error.config

      // Try to refresh token for Factorial ID users if it hasnt been tried yet
      if (
        isFactorialIdUser() &&
        originalRequest &&
        !('_retry' in originalRequest && originalRequest._retry)
      ) {
        // Set the retry flag to avoid infinite loops, only try to refresh token once
        Object.assign(originalRequest, { _retry: true })

        try {
          const refreshed = await refreshFactorialIdToken()

          if (refreshed) {
            // Retry the original request with the new token
            return axios.request(originalRequest)
          }
        } catch (refreshError) {
          // If refresh fails, fall through to session expired
        }
      }

      // If not a Factorial ID user or refresh failed, redirect to login
      sessionExpired(environment)

      return SKIP_ERROR
    } else if (responseStatus === 428) {
      differentUser(apolloClient)

      return SKIP_ERROR
    } else if (responseStatus === 422) {
      if (isTentacleError(responseData)) {
        throw new TentacleError(responseData)
      }

      if (isValidationErrorData(responseData)) {
        throw new ValidationError(responseData)
      }

      throw error
    } else if (responseStatus === 400 && isValidationErrorData(responseData)) {
      throw new ValidationError(responseData)
    } else if (responseStatus >= 400 && responseStatus < 500) {
      throw new BusinessError(parseErrorData(responseData))
    } else {
      reportToDatadog(trackingData, environment, error)
    }

    throw new NetworkError(error.message)
  }

const axiosInterceptors = (
  environment: Environment,
  apolloClient: ApolloClient<NormalizedCacheObject>
) => {
  axios.interceptors.request.use(beforeRequest(apolloClient, environment.releaseVersion))
  axios.interceptors.response.use(beforeResponse(apolloClient), onError(environment, apolloClient))
  // TODO: Remove after REST API returns string IDs during BigInt migration
  axios.interceptors.response.use((response) => {
    const responseType = response.config?.responseType
    if (responseType === 'blob' || responseType === 'arraybuffer') return response
    response.data = convertIdsToBigint(response.data)
    return response
  })
}

export default axiosInterceptors
